/**
 * LivePulse — feed-status badge with blinking-dot indicator.
 *
 * Fork Tier-A-#1 (A.4). Inspired by Stripe/Vercel "live" tags, re-skinned
 * in Butler palette (midway-cyan for live, glow-yellow for paused, rose
 * for offline, neutral ink for upcoming/closed/connecting).
 *
 * Semantic status prop drives everything — dot color, animation, caption,
 * and the accessible label — so the badge can never say one thing visually
 * and another thing to a screen reader. Reduced-motion users get a static
 * dot in the same semantic color; the label still reflects real feed state.
 *
 * Codex review evolution (2026-04-19):
 *   - pass 2: moved "Last scan" default → "Last refresh" (generated_at
 *     semantics)
 *   - pass 3: made the region a single aria-label instead of a polite
 *     live-region that re-announces every 10 s
 *   - pass 4: replaced the caption+static split with a semantic `status`
 *     prop so the dot color and accessible label track actual feed state
 *     (not animation availability)
 */

import { useEffect, useState } from 'react'

export type FeedStatus =
  | 'live'
  | 'connecting'
  | 'paused'
  | 'offline'
  | 'upcoming'
  | 'closed'
  | 'idle'

export interface LivePulseProps {
  /**
   * Feed status — the single source of truth for dot color, animation,
   * and the accessible label. Default 'live' matches the original
   * happy-path behavior.
   */
  status?: FeedStatus
  /**
   * ISO timestamp of the most recent data refresh. The backend currently
   * stamps this at response-build time (`generated_at`), not at actual
   * last-scan time, so the default caption reads "Last refresh Ns ago"
   * to stay honest. Pass `action="scan"` when a true per-scan timestamp
   * is plumbed through (not as of 2026-04-19).
   */
  lastScanAt?: string | null
  /**
   * Override the visible caption entirely. The aria-label still tracks
   * `status` so the screen-reader experience stays correct regardless of
   * what sighted users see.
   */
  caption?: string
  /** Noun for the default live caption — "refresh" (default) or "scan". */
  action?: 'refresh' | 'scan'
  'data-testid'?: string
}

interface StatusVisuals {
  /** Background dot Tailwind class. */
  dot: string
  /** Whether to blink (subject to prefers-reduced-motion). */
  animate: boolean
  /** Short human-readable default label — used when `caption` is absent. */
  defaultLabel: string
  /** Accessible label — spoken ONCE per status change (not per tick). */
  ariaLabel: string
}

function statusVisuals(status: FeedStatus): StatusVisuals {
  switch (status) {
    case 'live':
      return { dot: 'bg-midway-500', animate: true, defaultLabel: 'Live', ariaLabel: 'Live feed' }
    case 'connecting':
      return {
        dot: 'bg-ink-soft',
        animate: true,
        defaultLabel: 'Connecting…',
        ariaLabel: 'Connecting to feed',
      }
    case 'paused':
      return {
        dot: 'bg-glow-600',
        animate: false,
        defaultLabel: 'Feed paused',
        ariaLabel: 'Feed paused',
      }
    case 'offline':
      return {
        dot: 'bg-rose-500',
        animate: false,
        defaultLabel: 'Feed offline',
        ariaLabel: 'Feed offline',
      }
    case 'upcoming':
      return {
        dot: 'bg-ink-soft',
        animate: false,
        defaultLabel: 'Upcoming',
        ariaLabel: 'Event upcoming',
      }
    case 'closed':
      return {
        dot: 'bg-ink-soft',
        animate: false,
        defaultLabel: 'Closed',
        ariaLabel: 'Event closed',
      }
    case 'idle':
    default:
      return {
        dot: 'bg-ink-soft',
        animate: false,
        defaultLabel: 'Idle',
        ariaLabel: 'Feed idle',
      }
  }
}

function statusSkin(status: FeedStatus): string {
  // Wrapper border + background — matches the dot's semantic family.
  switch (status) {
    case 'live':
      return 'border-midway-200 bg-midway-50 text-midway-700 dark:border-midway-700 dark:bg-midway-900/30 dark:text-midway-200'
    case 'paused':
      return 'border-glow-300 bg-glow-50 text-butler-700 dark:border-glow-700 dark:bg-glow-900/30 dark:text-glow-200'
    case 'offline':
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-900/30 dark:text-rose-200'
    case 'connecting':
    case 'upcoming':
    case 'closed':
    case 'idle':
    default:
      return 'border-hairline bg-surface-stripe text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
  }
}

function secondsSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  const delta = Math.floor((now - t) / 1000)
  return delta >= 0 ? delta : 0
}

function formatAgo(sec: number): string {
  if (sec < 5) return 'just now'
  if (sec < 60) return `${sec} sec ago`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} hr ago`
  const d = Math.floor(h / 24)
  return `${d} day${d === 1 ? '' : 's'} ago`
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [])

  return reduced
}

export function LivePulse({
  status = 'live',
  lastScanAt,
  caption,
  action = 'refresh',
  'data-testid': testId = 'live-pulse',
}: LivePulseProps) {
  const reducedMotion = usePrefersReducedMotion()
  const [now, setNow] = useState<number>(() => Date.now())

  const visuals = statusVisuals(status)
  const skin = statusSkin(status)
  // Animation fires only when the status says it should AND the user
  // hasn't opted out. Reduced-motion users keep the semantic color.
  const blink = visuals.animate && !reducedMotion

  useEffect(() => {
    // Only tick the "Ns ago" caption while the feed is live — no need to
    // wake React for upcoming/closed/offline states where the caption is
    // not time-based.
    if (status !== 'live' || !lastScanAt) return undefined
    const id = window.setInterval(() => setNow(Date.now()), 10_000)
    return () => window.clearInterval(id)
  }, [status, lastScanAt])

  const ago = status === 'live' ? secondsSince(lastScanAt, now) : null
  const actionLabel = action === 'scan' ? 'Last scan' : 'Last refresh'
  const defaultCaption =
    status === 'live' && ago !== null
      ? `Live · ${actionLabel} ${formatAgo(ago)}`
      : visuals.defaultLabel
  const resolvedCaption = caption ?? defaultCaption

  const dotClass = blink
    ? `h-2 w-2 rounded-full ${visuals.dot} animate-marquee-blink`
    : `h-2 w-2 rounded-full ${visuals.dot}`

  return (
    <span
      data-testid={testId}
      data-status={status}
      data-motion={blink ? 'blink' : 'static'}
      role="status"
      aria-label={visuals.ariaLabel}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${skin}`}
    >
      <span aria-hidden="true" className={dotClass} />
      <span aria-hidden="true">{resolvedCaption}</span>
    </span>
  )
}
